import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  FormControlLabel,
  InputAdornment,
  Checkbox,
  Divider,
} from "@mui/material";
import {
  Edit,
  Category as CategoryIcon,
  Label,
  Source,
  Description,
  Link as LinkIcon,
  Comment,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CalendarMonth } from "@mui/icons-material";

// Validation Schema
const validationSchema = Yup.object({
  Category: Yup.string().required("Category is required"),
  Subcategory: Yup.string().required("Subcategory is required"),
  Name: Yup.string()
    .min(3, "Name must be at least 3 characters")
    .required("Name is required"),
  Source: Yup.string()
    .min(2, "Source must be at least 2 characters")
    .required("Source is required"),
  "Short Description": Yup.string()
    .min(10, "Description must be at least 10 characters")
    .required("Short Description is required"),
  Hyperlink: Yup.string()
    .url("Must be a valid URL")
    .required("Hyperlink is required"),
});

const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  return date.toISOString().split('T')[0];  // convert to YYYY-MM-DD
};

const EditRowModal = ({ open, onClose, rowData, rowIndex, onUpdate }) => {
  const formik = useFormik({
    initialValues: {
      Category: rowData?.Category || "",
      Subcategory: rowData?.["Subcategory"] || "",
      Name: rowData?.Name || "",
      Source: rowData?.Source || "",
      "Short Description": rowData?.["Short Description"] || "",
      Hyperlink: rowData?.Hyperlink || "",
      "Last Date to Apply": formatDateForInput(rowData?.["Last Date to Apply"]),
      Recurring: rowData?.["Recurring"] || "No",
      Approved: rowData?.Approved || "No",
      Feature: rowData?.Feature || "No",
      "Internal Comments": rowData?.["Internal Comments"] || "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const formattedValues = { ...values };

      // Convert YYYY-MM-DD → DD-MM-YYYY
      if (values["Last Date to Apply"]) {
        const [yyyy, mm, dd] = values["Last Date to Apply"].split("-");
        formattedValues["Last Date to Apply"] = `${dd}-${mm}-${yyyy}`;
      }
      onUpdate(rowIndex, formattedValues);
      onClose();
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "rgba(30, 58, 95, 0.05)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          pb: 2,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: "#1e3a5f",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Edit sx={{ color: "white", fontSize: "1.2rem" }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e3a5f" }}>
            Edit Row {rowIndex + 1}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Update the opportunity details below
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Grid container spacing={2.5}>
            {/* Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="Name"
                name="Name"
                label="Opportunity Name"
                value={formik.values.Name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.Name && Boolean(formik.errors.Name)}
                helperText={formik.touched.Name && formik.errors.Name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Description sx={{ color: "#1e3a5f" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#1e3a5f",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a5f",
                    },
                  },
                }}
              />
            </Grid>

            {/* Category */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="Category"
                name="Category"
                label="Category"
                value={formik.values.Category}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.Category && Boolean(formik.errors.Category)
                }
                helperText={formik.touched.Category && formik.errors.Category}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CategoryIcon sx={{ color: "#1e3a5f" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#1e3a5f",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a5f",
                    },
                  },
                }}
              />
            </Grid>

            {/* Subcategory */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="Subcategory"
                name="Subcategory"
                label="Subcategory"
                value={formik.values["Subcategory"]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched["Subcategory"] &&
                  Boolean(formik.errors["Subcategory"])
                }
                helperText={
                  formik.touched["Subcategory"] && formik.errors["Subcategory"]
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Label sx={{ color: "#1e3a5f" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#1e3a5f",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a5f",
                    },
                  },
                }}
              />
            </Grid>

            {/* Source */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="Source"
                name="Source"
                label="Source"
                value={formik.values.Source}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.Source && Boolean(formik.errors.Source)}
                helperText={formik.touched.Source && formik.errors.Source}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Source sx={{ color: "#1e3a5f" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Short Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="Short Description"
                name="Short Description"
                label="Short Description"
                multiline
                rows={3}
                value={formik.values["Short Description"]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched["Short Description"] &&
                  Boolean(formik.errors["Short Description"])
                }
                helperText={
                  formik.touched["Short Description"] &&
                  formik.errors["Short Description"]
                }
              />
            </Grid>

            {/* Hyperlink */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="Hyperlink"
                name="Hyperlink"
                label="Hyperlink"
                value={formik.values.Hyperlink}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.Hyperlink && Boolean(formik.errors.Hyperlink)
                }
                helperText={formik.touched.Hyperlink && formik.errors.Hyperlink}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon sx={{ color: "#1e3a5f" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Last Date to Apply */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="Last Date to Apply"
                name="Last Date to Apply"
                label="Last Date to Apply (Optional)"
                type="date"
                value={formik.values["Last Date to Apply"] || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarMonth sx={{ color: "primary.main" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Additional Fields */}
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.Recurring === "Yes"}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "Recurring",
                        e.target.checked ? "Yes" : "No"
                      )
                    }
                    name="Recurring"
                  />
                }
                label="Recurring"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.Approved === "Yes"}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "Approved",
                        e.target.checked ? "Yes" : "No"
                      )
                    }
                    name="Approved"
                  />
                }
                label="Approved"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.Feature === "Yes"}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "Feature",
                        e.target.checked ? "Yes" : "No"
                      )
                    }
                    name="Feature"
                  />
                }
                label="Featured"
              />
            </Grid>

            {/* Internal Comments */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="Internal Comments"
                name="Internal Comments"
                label="Internal Comments"
                multiline
                rows={2}
                value={formik.values["Internal Comments"]}
                onChange={formik.handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Comment sx={{ color: "#1e3a5f" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          sx={{
            color: "#555",
            minWidth: 100,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          disabled={!formik.isValid || formik.isSubmitting}
          sx={{
            minWidth: 120,
            bgcolor: "#1e3a5f",
            "&:hover": {
              bgcolor: "#152d4a",
            },
          }}
        >
          Update Row
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRowModal;
